import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dropdown,
  Option,
  makeStyles,
  tokens,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import { Location24Regular, CheckmarkCircle20Regular, DismissCircle20Regular } from '@fluentui/react-icons';
import { useDevice } from '../../model/DeviceContext';
import { Eup } from '../../model/DeviceTypes';
import { useAppStyles } from '../styles/appStyles';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXS,
    paddingTop: 0,
    marginBottom: tokens.spacingVerticalS,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  dropdown: {
    minWidth: '100%',
    '& .fui-Option': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  singleEupDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  eupName: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    flex: 1,
  },
  eupOption: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
  },
  eupOptionText: {
    flex: 1,
  },
  eupPrefix: {
    fontStyle: 'italic',
    marginRight: tokens.spacingHorizontalXXS,
  },
  statusActive: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusInactive: {
    color: tokens.colorPaletteRedForeground1,
  },
});

interface EupsSelectorProps {
  companyId: string | null;
  onEupChange?: (eupId: string | null) => void;
  initialSelectedEupId?: string | null;
}

const EupsSelector: React.FC<EupsSelectorProps> = ({ companyId, onEupChange, initialSelectedEupId }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const appStyles = useAppStyles();
  const { subscription } = useDevice();

  // Initialize selectedEupId from localStorage (per company) or initialSelectedEupId
  const [selectedEupId, setSelectedEupId] = useState<string | null>(() => {
    if (initialSelectedEupId) {
      return initialSelectedEupId;
    }

    // Try to restore from localStorage using company-specific key
    if (companyId) {
      try {
        const storageKey = `carbowaste_selected_eup_${companyId}`;
        return localStorage.getItem(storageKey);
      } catch (error) {
        console.warn('Failed to read selected EUP from localStorage:', error);
      }
    }

    return null;
  });

  console.log('📍 EupsSelector RENDER:', {
    companyId,
    initialSelectedEupId,
    hasSubscription: !!subscription,
    companiesCount: subscription?.companies?.length || 0
  });

  // Find company's EUPs based on companyId and sort by identificationNumberPrefix numerically
  const company = subscription?.companies?.find(c => c.companyId === companyId);
  const eups = (company?.eups || []).slice().sort((a, b) => {
    // Convert identificationNumberPrefix to numbers for proper numeric sorting
    const numA = parseFloat(a.identificationNumberPrefix) || 0;
    const numB = parseFloat(b.identificationNumberPrefix) || 0;
    return numA - numB;
  });

  console.log('📍 EupsSelector DATA:', {
    companyId,
    companyFound: !!company,
    companyName: company?.name,
    eupsCount: eups.length,
    eupsList: eups.map(e => `${e.eupId.substring(0, 8)}...${e.name}`),
    rawCompanyEups: company?.eups,
    subscriptionCompanies: subscription?.companies
  });

  // Debug logging
  useEffect(() => {
    console.log('📍 EupsSelector: subscription/companyId changed', {
      companyId,
      subscriptionExists: !!subscription,
      companiesCount: subscription?.companies?.length || 0,
      companyFound: !!company,
      companyName: company?.name,
      eupsCount: eups.length,
      eupIds: eups.map(e => e.eupId)
    });
  }, [subscription, companyId, company, eups]);

  // Load selectedEupId from localStorage when companyId changes
  useEffect(() => {
    if (!companyId) {
      setSelectedEupId(null);
      return;
    }

    // Try to restore from localStorage for this company
    try {
      const storageKey = `carbowaste_selected_eup_${companyId}`;
      const storedEupId = localStorage.getItem(storageKey);

      if (storedEupId) {
        console.log('📍 Restoring selectedEupId from localStorage:', { companyId, eupId: storedEupId });
        setSelectedEupId(storedEupId);
        if (onEupChange) {
          onEupChange(storedEupId);
        }
      } else {
        // No stored selection - will be handled by auto-select useEffect
        console.log('📍 No stored EUP found for company:', companyId);
        setSelectedEupId(null);
      }
    } catch (error) {
      console.warn('Failed to read selected EUP from localStorage:', error);
      setSelectedEupId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]); // Only run when companyId changes

  // Initialize or restore selection
  useEffect(() => {
    console.log('📍 EupsSelector: Initializing selection', {
      companyId,
      eupsCount: eups.length,
      initialSelectedEupId,
      currentSelectedEupId: selectedEupId
    });

    // Priority 1: Use initialSelectedEupId if provided and valid
    if (initialSelectedEupId && eups.some(e => e.eupId === initialSelectedEupId)) {
      console.log('📍 Setting EUP from initialSelectedEupId:', initialSelectedEupId);
      setSelectedEupId(initialSelectedEupId);
      if (onEupChange) {
        onEupChange(initialSelectedEupId);
      }

      // Save to localStorage
      if (companyId) {
        try {
          const storageKey = `carbowaste_selected_eup_${companyId}`;
          localStorage.setItem(storageKey, initialSelectedEupId);
        } catch (error) {
          console.warn('Failed to save initial EUP to localStorage:', error);
        }
      }
    }
    // Priority 2: Auto-select first EUP if none selected and EUPs available
    else if (!selectedEupId && eups.length > 0 && !initialSelectedEupId) {
      const firstEupId = eups[0].eupId;
      console.log('📍 Auto-selecting first EUP:', firstEupId);
      setSelectedEupId(firstEupId);
      if (onEupChange) {
        onEupChange(firstEupId);
      }

      // Save to localStorage
      if (companyId) {
        try {
          const storageKey = `carbowaste_selected_eup_${companyId}`;
          localStorage.setItem(storageKey, firstEupId);
        } catch (error) {
          console.warn('Failed to save auto-selected EUP to localStorage:', error);
        }
      }
    } else if (eups.length === 0) {
      console.warn('📍 No EUPs available for company:', companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedEupId, companyId, eups.length, subscription?.companies]); // Dodano subscription?.companies

  const handleEupChange = (event: any, data: any) => {
    const eupId = data.optionValue as string;
    setSelectedEupId(eupId);
    onEupChange?.(eupId);

    // Save to localStorage with company-specific key
    if (companyId) {
      try {
        const storageKey = `carbowaste_selected_eup_${companyId}`;
        localStorage.setItem(storageKey, eupId);
        console.log('📍 Saved selected EUP to localStorage:', { companyId, eupId });
      } catch (error) {
        console.warn('Failed to save selected EUP to localStorage:', error);
      }
    }
  };

  if (!companyId || eups.length === 0) {
    return null;
  }

  const isSingleEup = eups.length === 1;
  const selectedEup = eups.find(e => e.eupId === selectedEupId);

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        <Location24Regular />
        <Text>
          {isSingleEup
            ? t('eups_selector.label_single', 'Miejsce prowadzenia działalności')
            : t('eups_selector.label', 'Wybierz miejsce prowadzenia działalności')
          }
        </Text>
      </label>

      {isSingleEup ? (
        <div className={styles.singleEupDisplay}>
          <Text className={styles.eupName}>
            <span className={styles.eupPrefix}>{eups[0].identificationNumberPrefix}</span>
            {" | "}
            {eups[0].name}
          </Text>
          {eups[0].isActive ? (
            <Tooltip
              content={
                <div className={appStyles.tooltipContentActive}>
                  {t("bdo_entities.status.active", "Aktywny")}
                </div>
              }
              relationship="label"
            >
              <CheckmarkCircle20Regular className={styles.statusActive} />
            </Tooltip>
          ) : (
            <Tooltip
              content={
                <div className={appStyles.tooltipContentInactive}>
                  {t("bdo_entities.status.inactive", "Nieaktywny")}
                </div>
              }
              relationship="label"
            >
              <DismissCircle20Regular className={styles.statusInactive} />
            </Tooltip>
          )}
        </div>
      ) : (
        <Dropdown
          className={styles.dropdown}
          placeholder={t('eups_selector.placeholder', 'Wybierz miejsce prowadzenia działalności...')}
          value={selectedEup ? `${selectedEup.identificationNumberPrefix} | ${selectedEup.name}` : ''}
          selectedOptions={selectedEupId ? [selectedEupId] : []}
          onOptionSelect={handleEupChange}
        >
          {eups.map((eup) => (
            <Option key={eup.eupId} value={eup.eupId} text={`${eup.identificationNumberPrefix} | ${eup.name}`}>
              <div className={styles.eupOption}>
                <span className={styles.eupOptionText}>
                  <span className={styles.eupPrefix}>{eup.identificationNumberPrefix}</span>
                  {" | "}
                  {eup.name}
                </span>
                {eup.isActive ? (
                  <Tooltip
                    content={
                      <div className={appStyles.tooltipContentActive}>
                        {t("bdo_entities.status.active", "Aktywny")}
                      </div>
                    }
                    relationship="label"
                  >
                    <CheckmarkCircle20Regular className={styles.statusActive} />
                  </Tooltip>
                ) : (
                  <Tooltip
                    content={
                      <div className={appStyles.tooltipContentInactive}>
                        {t("bdo_entities.status.inactive", "Nieaktywny")}
                      </div>
                    }
                    relationship="label"
                  >
                    <DismissCircle20Regular className={styles.statusInactive} />
                  </Tooltip>
                )}
              </div>
            </Option>
          ))}
        </Dropdown>
      )}
    </div>
  );
};

export default EupsSelector;
